import React from "react";
import {
  Flex,
  Text,
  Card,
  ProgressBar,
  Button,
  Divider,
  Icon,
  OverflowText,
  Tooltip,
  isNullish
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { formatCurrency } from "@/Utils/formatters";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import {
  kDesiredHolders,
  kEmployeeSalary,
  kPositionBudgetedSalary,
  kPositionCostBandMaximum
} from "@coreapp/constants";
import { isBlockedField, getBlockedFieldDisplayValue } from "@coreapp/utils/FormatFieldValue";
import {
  BudgetStatus,
  BudgetStatusPill
} from "@/appComponents/Blocks/Modals/RecordEditorModal/Components/BudgetStatusPill";
import {
  calculateTotalPositionBudget,
  isUserMappedField,
  isCostBandMapped,
  getPositionBudgetTooltip as getPositionBudgetTooltipUtil
} from "../PositionEditorUtils";
import { PLACEHOLDER_TABLE_VALUE } from "@/Utils/Constants";
import { CustomTooltip } from "@/appComponents/Blocks/Composites/CustomTooltip/CustomTooltip";

/**
 * Calculates budget utilization percentages for the progress bar
 * @param {number} actual - The actual cost
 * @param {number} budget - The budget amount
 * @returns {Object} Object containing percentage values for the progress bars
 */
const calculateBudgetPercentages = (actual, budget) => {
  const budgetIsUnset = budget === null || budget === undefined;

  // If no actual cost, return defaults
  if (!actual) {
    return {
      isOverBudget: false,
      underBudgetPercent: 0,
      overBudgetPercent: 0,
      percentageUsed: 0,
      noBudget: budgetIsUnset
    };
  }

  // If budget not set, show unset --
  if (budgetIsUnset) {
    return {
      isOverBudget: true,
      underBudgetPercent: 0,
      overBudgetPercent: 0,
      percentageUsed: 0,
      noBudget: true
    };
  }

  // handle explicit 0 set
  if (budget === 0) {
    return {
      isOverBudget: true,
      underBudgetPercent: 0,
      overBudgetPercent: 100,
      percentageUsed: 0,
      noBudget: true
    };
  }

  const isOverBudget = actual > budget;

  if (isOverBudget) {
    // Calculate actual percentage of budget used
    const percentageUsed = (actual / budget) * 100;

    const budgetPercent = Math.min((budget / actual) * 100, 100);
    const overBudgetPercent = 100 - budgetPercent;

    return {
      isOverBudget: true,
      underBudgetPercent: budgetPercent,
      overBudgetPercent: overBudgetPercent,
      percentageUsed: percentageUsed,
      noBudget: false
    };
  } else {
    // Calculate percentages for under budget scenario
    // Dark orange for used budget, light orange for remaining
    const usedPercent = (actual / budget) * 100;

    return {
      isOverBudget: false,
      underBudgetPercent: usedPercent,
      overBudgetPercent: 0,
      percentageUsed: usedPercent,
      noBudget: false
    };
  }
};

const formatBudgetValue = (value, isMasked, options) => {
  if (isMasked) return getBlockedFieldDisplayValue();
  if (value === null || value === undefined) return PLACEHOLDER_TABLE_VALUE;
  return formatCurrency(value, options);
};

/**
 * BudgetDetailsDrawer Component - Position Budget Slide-out Panel
 */
const BudgetDetailsDrawer = ({
  isOpen,
  position,
  assignedEmployees = [],
  originalAssignedEmployees = null, // Optional: for showing proposed changes
  dataTestIdPrefix = "position-budget-drawer",
  getPositionFieldValue = undefined // Optional: function to get real-time position field values
}) => {
  if (!isOpen) return null;

  const doc = GetDoc();
  const iWizData = doc.GetIWizData();
  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();

  const launchRecordEditor = dataObj => {
    const overrideObj = planMgr.GetActiveOverrideOrCreate(dataObj);
    planMgr.handleRecord(overrideObj, dataObj);
  };

  const parseFieldValue = value => {
    if (isNullish(value) || value === "") {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const getPositionBudgetedSalary = () => {
    const value = getPositionFieldValue
      ? getPositionFieldValue(kPositionBudgetedSalary)
      : position.SmartGetFieldValue(kPositionBudgetedSalary);
    return parseFieldValue(value);
  };

  const getPositionCostBandMaximum = () => {
    const value = getPositionFieldValue
      ? getPositionFieldValue(kPositionCostBandMaximum)
      : position.SmartGetFieldValue(kPositionCostBandMaximum);
    return parseFieldValue(value);
  };

  const getDesiredHolders = () => {
    if (getPositionFieldValue) {
      return getPositionFieldValue(kDesiredHolders) || 1;
    }
    return position.SmartGetFieldValue(kDesiredHolders) || 1;
  };

  const costBandEnabled = iWizData?.fPositionConfig?.useCostBand() || false;
  const maxCostBand = getPositionCostBandMaximum();

  const isShowingProposedChanges = originalAssignedEmployees !== null;

  const costBandsDriveBudget =
    !isUserMappedField(kPositionBudgetedSalary) && isCostBandMapped(kPositionCostBandMaximum);
  // Sentinel only lives on the saved record; the override path can't carry it.
  const isPositionCostBandBlocked = isBlockedField(
    position.SmartGetFieldValue(kPositionCostBandMaximum)
  );
  const isPositionBudgetInputBlocked =
    isBlockedField(position.SmartGetFieldValue(kPositionBudgetedSalary)) ||
    (costBandsDriveBudget && isPositionCostBandBlocked);
  const isAnyAssigneeSalaryBlocked = assignedEmployees.some(employee =>
    isBlockedField(employee.SmartGetFieldValue(kEmployeeSalary))
  );
  const isAnyOriginalSalaryBlocked =
    isShowingProposedChanges &&
    originalAssignedEmployees.some(employee =>
      isBlockedField(employee.SmartGetFieldValue(kEmployeeSalary))
    );
  const isBudgetMasked =
    isPositionBudgetInputBlocked || isAnyAssigneeSalaryBlocked || isAnyOriginalSalaryBlocked;

  const getTotalPositionBudget = () => {
    if (!planMgr) {
      return calculateTotalPositionBudget(
        getPositionBudgetedSalary,
        getPositionCostBandMaximum,
        getDesiredHolders
      );
    }

    const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
    const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

    if (!isPositionBudgetMapped && isCostBandsMapped) {
      // Scenario 4: Cost bands ARE mapped and position budget is NOT mapped
      return calculateTotalPositionBudget(
        getPositionBudgetedSalary,
        getPositionCostBandMaximum,
        getDesiredHolders
      );
    } else {
      // Scenarios 1, 2, 3: Use actual position budget value (even if 0 or empty)
      const budgetedSalary = getPositionBudgetedSalary();
      return budgetedSalary === null ? null : budgetedSalary || 0;
    }
  };

  const totalPositionBudget = getTotalPositionBudget();

  const getPositionBudgetTooltip = () => {
    if (!planMgr) {
      return formatBudgetValue(totalPositionBudget, isPositionBudgetInputBlocked);
    }
    return getPositionBudgetTooltipUtil(position);
  };

  const calculateEmployeeTotalCost = employees => {
    if (employees.some(e => isBlockedField(e.SmartGetFieldValue(kEmployeeSalary)))) {
      return null;
    }
    return employees.reduce((total, employee) => {
      const raw = parseFloat(employee.SmartGetFieldValue(kEmployeeSalary));
      const salary = isNaN(raw) ? 0 : raw;
      return total + salary;
    }, 0);
  };

  const originalPositionCost = isShowingProposedChanges
    ? calculateEmployeeTotalCost(originalAssignedEmployees)
    : 0;

  // Calculate total position cost (sum of all employee salaries)
  const totalPositionCost = calculateEmployeeTotalCost(assignedEmployees);

  // Calculate remaining budget
  const remainingBudget =
    isBudgetMasked || totalPositionBudget === null || totalPositionCost === null
      ? null
      : totalPositionBudget - totalPositionCost;

  // Calculate budget percentages for the progress bar
  const budgetPercentages = calculateBudgetPercentages(totalPositionCost, totalPositionBudget);

  // Calculate separate bars for original vs proposed changes
  const calculateProposedBudgetBars = () => {
    if (!isShowingProposedChanges || isBudgetMasked) {
      return null;
    }

    const originalPercent =
      totalPositionBudget > 0 ? (originalPositionCost / totalPositionBudget) * 100 : 0;
    const proposedDelta = totalPositionCost - originalPositionCost;
    const proposedDeltaPercent =
      totalPositionBudget > 0 ? (proposedDelta / totalPositionBudget) * 100 : 0;

    // Calculate what we'll actually show based on proposed total
    const finalPercent = originalPercent + proposedDeltaPercent;
    const finalWithinBudget = Math.min(finalPercent, 100);
    const finalOverBudget = Math.max(0, finalPercent - 100);

    // For display, show original up to the final amount, then proposed changes
    let displayOriginalWithinBudget, displayOriginalOverBudget;
    let proposedWithinBudget = 0;
    let proposedOverBudget = 0;

    if (proposedDeltaPercent >= 0) {
      // Adding cost - show full original + striped proposed
      displayOriginalWithinBudget = Math.min(originalPercent, 100);
      displayOriginalOverBudget = Math.max(0, originalPercent - 100);

      const remainingBudgetSpace = Math.max(0, 100 - originalPercent);
      proposedWithinBudget = Math.min(proposedDeltaPercent, remainingBudgetSpace);
      proposedOverBudget = Math.max(0, proposedDeltaPercent - remainingBudgetSpace);
    } else {
      // Removing cost - show reduced original amounts (no striped bars)
      displayOriginalWithinBudget = finalWithinBudget;
      displayOriginalOverBudget = finalOverBudget;
    }

    return {
      original: {
        withinBudget: displayOriginalWithinBudget,
        overBudget: displayOriginalOverBudget
      },
      proposed: {
        withinBudget: proposedWithinBudget,
        overBudget: proposedOverBudget
      },
      hasProposedChanges: Math.abs(proposedDelta) > 0.01 // Small threshold for floating point
    };
  };

  const proposedBars = calculateProposedBudgetBars();

  // Determine employee status for proposed changes
  const getEmployeeStatus = employee => {
    if (!isShowingProposedChanges) return "existing";

    const employeeId =
      employee.SmartGetFieldValue("EmployeeID") || employee.SmartGetFieldValue("RecordID");
    const originalIds = originalAssignedEmployees.map(
      emp => emp.SmartGetFieldValue("EmployeeID") || emp.SmartGetFieldValue("RecordID")
    );

    return originalIds.includes(employeeId) ? "existing" : "new";
  };
  const shortNumberFormatOptions = {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  };
  return (
    <Flex className="oc-budget-details-drawer" direction={"column"} dataTestId={dataTestIdPrefix}>
      {/* Position Budget Summary Card */}
      <Card
        dataTestId={`${dataTestIdPrefix}-summary-card`}
        className={"oc-budget-details-summary-card"}
      >
        <Flex direction="column" gap="16px">
          <Flex justifyContent="space-between" alignItems="flex-end">
            <Flex direction={"column"} gap="4px">
              <Text size="medium">{appstr("Current position cost")}</Text>
              <Tooltip message={formatBudgetValue(totalPositionCost, isAnyAssigneeSalaryBlocked)}>
                <Text element="h2" modifier="medium" size="small" type="display">
                  {formatBudgetValue(
                    totalPositionCost,
                    isAnyAssigneeSalaryBlocked,
                    shortNumberFormatOptions
                  )}
                </Text>
              </Tooltip>
            </Flex>
            {remainingBudget !== 0 && !budgetPercentages.noBudget && !isBudgetMasked && (
              <Flex className={"oc-shrink-0"} justifyContent={"flex-end"}>
                <BudgetStatusPill
                  status={
                    budgetPercentages.isOverBudget
                      ? BudgetStatus.overbudget
                      : BudgetStatus.underbudget
                  }
                />
              </Flex>
            )}
          </Flex>

          {/* Budget Progress Bar */}
          <Flex justifyContent={"space-between"} gap={"8px"}>
            <div
              style={{
                borderRadius: "2px",
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
                width: "100%"
              }}
              dataTestId={`${dataTestIdPrefix}-budget-progress`}
            >
              {isBudgetMasked ? (
                <ProgressBar max={100} min={0} modifier="default" value={0} />
              ) : proposedBars?.hasProposedChanges ? (
                // Showing proposed changes - separate bars for original vs proposed
                (() => {
                  // Check if there are any proposed changes (like isOverBudget in normal view)
                  const hasProposedChanges =
                    proposedBars.proposed.withinBudget > 0 || proposedBars.proposed.overBudget > 0;

                  // Calculate total used percentage
                  const totalUsed =
                    proposedBars.original.withinBudget + proposedBars.proposed.withinBudget;

                  // Calculate remaining budget (only when under 100% and no over budget)
                  const isOverBudget =
                    proposedBars.original.overBudget > 0 || proposedBars.proposed.overBudget > 0;
                  const remainingBudget = !isOverBudget && totalUsed < 100 ? 100 - totalUsed : 0;

                  return (
                    <>
                      {/* Original within budget bar - stacks only when there are proposed changes */}
                      {proposedBars.original.withinBudget >= 0 && (
                        <ProgressBar
                          max={100}
                          min={0}
                          modifier="default"
                          progessOnly={(hasProposedChanges && !isOverBudget) || isOverBudget}
                          value={proposedBars.original.withinBudget}
                          style={{
                            backgroundColor: "#F57C00"
                          }}
                        />
                      )}
                      {/* Proposed within budget bar (striped) - creates container when under budget */}
                      {proposedBars.proposed.withinBudget > 0 && (
                        <ProgressBar
                          max={isOverBudget ? 100 : 100 - proposedBars.original.withinBudget}
                          min={0}
                          modifier="default"
                          progessOnly={isOverBudget}
                          striped={true}
                          value={proposedBars.proposed.withinBudget}
                          style={{
                            backgroundColor: "#F57C00"
                          }}
                        />
                      )}
                      {/* Original over budget bar - always stacks when there are proposed changes */}
                      {proposedBars.original.overBudget > 0 && (
                        <ProgressBar
                          max={100}
                          min={0}
                          modifier="error"
                          progessOnly={true}
                          value={proposedBars.original.overBudget}
                        />
                      )}
                      {/* Proposed over budget bar (striped) - always stacks */}
                      {proposedBars.proposed.overBudget > 0 && (
                        <ProgressBar
                          max={100}
                          min={0}
                          modifier="error"
                          progessOnly={true}
                          striped={true}
                          value={proposedBars.proposed.overBudget}
                        />
                      )}
                    </>
                  );
                })()
              ) : // Normal view - single bar(s)
              budgetPercentages.noBudget ? (
                // No budget set == empty bar
                <ProgressBar max={100} min={0} modifier="default" value={0} />
              ) : (
                <>
                  <ProgressBar
                    max={100}
                    min={0}
                    modifier="default"
                    progessOnly={budgetPercentages.isOverBudget}
                    value={budgetPercentages.underBudgetPercent}
                    style={{
                      backgroundColor: budgetPercentages.isOverBudget ? "#F57C00" : "#F57C00"
                    }}
                  />
                  {budgetPercentages.isOverBudget && (
                    <ProgressBar
                      max={100}
                      min={0}
                      modifier="error"
                      progessOnly
                      value={budgetPercentages.overBudgetPercent}
                    />
                  )}
                </>
              )}
            </div>
            <Text size="medium" className="oc-shrink-0">
              {isBudgetMasked
                ? getBlockedFieldDisplayValue()
                : budgetPercentages.noBudget
                  ? "--%"
                  : budgetPercentages.percentageUsed > 999
                    ? "999+%"
                    : `${Math.round(budgetPercentages.percentageUsed)}%`}
            </Text>
          </Flex>
          {/* Budget Details */}
          <Flex justifyContent="space-between" alignItems="center" gap={"8px"}>
            <Tooltip message={getPositionBudgetTooltip()}>
              <Flex gap="4px" flex={"1"} overflow={"hidden"} alignItems={"center"}>
                <div className="oc-budget-info-icon">
                  <Icon name={"circle-info"} size={"small"} />
                </div>
                <Text size="small" colorModifier="grey" modifier={"bold"} className={"oc-shrink-0"}>
                  {appstrp("total budget", ":")}
                </Text>
                <OverflowText size="small" colorModifier="grey" modifier={"bold"}>
                  {formatBudgetValue(
                    totalPositionBudget,
                    isPositionBudgetInputBlocked,
                    shortNumberFormatOptions
                  )}
                </OverflowText>
              </Flex>
            </Tooltip>
            <Flex
              gap="4px"
              flex={"1"}
              justifyContent={"flex-end"}
              overflow={"hidden"}
              alignItems={"center"}
            >
              <Text size="small" colorModifier="grey" modifier={"bold"} className={"oc-shrink-0"}>
                {appstrp("remaining", ":")}
              </Text>
              <Tooltip message={formatBudgetValue(remainingBudget, isBudgetMasked)}>
                <Text
                  size="small"
                  colorModifier={remainingBudget !== null && remainingBudget < 0 ? "error" : "grey"}
                  modifier={"bold"}
                >
                  {formatBudgetValue(remainingBudget, isBudgetMasked, shortNumberFormatOptions)}
                </Text>
              </Tooltip>
            </Flex>
          </Flex>
        </Flex>
      </Card>
      <Divider />
      {/* Employee Budget Cards */}
      {assignedEmployees.length > 0 && (
        <div
          dataTestId={`${dataTestIdPrefix}-employee-cards`}
          className={"oc-budget-employee-container"}
        >
          <Flex direction="column" gap="16px">
            {assignedEmployees.map((employee, index) => {
              const salaryValue = employee.SmartGetFieldValue("Employee Salary");

              const salaryValueBlocked = isBlockedField(salaryValue);

              const employeeSalary = parseFloat(salaryValue || 0);

              const employeeCostBandValue = employee.SmartGetFieldValue(
                "Position Cost Band Maximum"
              );
              const parsedEmployeeCostBand = parseFieldValue(employeeCostBandValue);
              const isEmployeeCostBandBlocked =
                isBlockedField(employeeCostBandValue) ||
                (parsedEmployeeCostBand === null && isPositionCostBandBlocked);
              const employeeMaxCostBand = isEmployeeCostBandBlocked
                ? null
                : (parsedEmployeeCostBand ?? maxCostBand);
              const isEmployeeBudgetMasked = salaryValueBlocked || isEmployeeCostBandBlocked;

              const remainingBudgetForEmployee =
                isEmployeeBudgetMasked || employeeMaxCostBand === null
                  ? null
                  : employeeMaxCostBand - employeeSalary;
              const employeePercentages = salaryValueBlocked
                ? {
                    isOverBudget: false,
                    underBudgetPercent: 0,
                    overBudgetPercent: 0,
                    percentageUsed: 0,
                    noBudget: true
                  }
                : calculateBudgetPercentages(employeeSalary, employeeMaxCostBand);
              const employeeStatus = getEmployeeStatus(employee);
              const isNewEmployee = employeeStatus === "new";

              return (
                <Card
                  key={employee.SmartGetFieldValue("EmployeeID") || index}
                  dataTestId={`${dataTestIdPrefix}-employee-card-${index}`}
                  className={"oc-budget-details-employee-card"}
                >
                  <Flex direction="column" gap="12px">
                    <Flex direction={"column"} gap="4px" alignItems={"flex-start"}>
                      <Button
                        size={"medium"}
                        modifier={"underline"}
                        onClick={() => launchRecordEditor(employee)}
                      >
                        {employee.FormatEmployeeLabel(["Name", "EmployeeID"]) || appstr("Employee")}
                      </Button>
                      <Flex alignItems="center" gap={"8px"}>
                        <Flex gap={"4px"}>
                          <Tooltip message={formatBudgetValue(employeeSalary, salaryValueBlocked)}>
                            <Text size="large" modifier={"bold"}>
                              {formatBudgetValue(
                                employeeSalary,
                                salaryValueBlocked,
                                shortNumberFormatOptions
                              )}
                            </Text>
                          </Tooltip>
                          {salaryValueBlocked && (
                            <CustomTooltip
                              messageKey="compensationaccesstip"
                              buttonProps={{ className: "oc-budget-info-icon" }}
                            />
                          )}
                        </Flex>
                        {remainingBudgetForEmployee !== null &&
                          remainingBudgetForEmployee !== 0 &&
                          !employeePercentages.noBudget && (
                            <Icon
                              layout="default"
                              modifier={employeePercentages.isOverBudget ? "error" : "success"}
                              name={
                                employeePercentages.isOverBudget
                                  ? "arrow-up-regular"
                                  : "arrow-down-regular"
                              }
                              size="small"
                            />
                          )}
                      </Flex>
                    </Flex>

                    {/* Employee Budget Progress Bar */}
                    <Flex gap={"8px"} justifyContent="space-between">
                      <div
                        style={{
                          borderRadius: "2px",
                          display: "flex",
                          flexDirection: "row",
                          overflow: "hidden",
                          width: "100%"
                        }}
                        dataTestId={`${dataTestIdPrefix}-employee-progress-${index}`}
                      >
                        {employeePercentages.noBudget ? (
                          // No budget set - show empty bar
                          <ProgressBar
                            max={100}
                            min={0}
                            modifier="default"
                            value={0}
                            striped={isNewEmployee}
                          />
                        ) : (
                          <>
                            <ProgressBar
                              max={100}
                              min={0}
                              modifier="default"
                              progessOnly={employeePercentages.isOverBudget}
                              value={employeePercentages.underBudgetPercent}
                              striped={isNewEmployee}
                              style={{
                                backgroundColor: employeePercentages.isOverBudget
                                  ? "#F57C00"
                                  : "#F57C00"
                              }}
                            />
                            {employeePercentages.isOverBudget && (
                              <ProgressBar
                                max={100}
                                min={0}
                                modifier="error"
                                progessOnly
                                striped={isNewEmployee}
                                value={employeePercentages.overBudgetPercent}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <Text size="medium" className="oc-shrink-0">
                        {isEmployeeBudgetMasked
                          ? getBlockedFieldDisplayValue()
                          : employeePercentages.noBudget
                            ? "--%"
                            : employeePercentages.percentageUsed > 999
                              ? "999+%"
                              : `${Math.round(employeePercentages.percentageUsed)}%`}
                      </Text>
                    </Flex>
                    {/* Employee Budget Details */}
                    <Flex justifyContent="space-between" alignItems="center" gap={"8px"}>
                      <Flex gap="4px" flex={"1"} alignItems={"center"}>
                        <Text
                          size="small"
                          colorModifier="grey"
                          modifier={"bold"}
                          className={"oc-shrink-0"}
                        >
                          {appstrp("budget", ":")}
                        </Text>
                        <Tooltip
                          message={formatBudgetValue(
                            employeeMaxCostBand,
                            isEmployeeCostBandBlocked
                          )}
                        >
                          <Text size="small" colorModifier="grey" modifier={"bold"}>
                            {formatBudgetValue(
                              employeeMaxCostBand,
                              isEmployeeCostBandBlocked,
                              shortNumberFormatOptions
                            )}
                          </Text>
                        </Tooltip>
                      </Flex>
                      <Flex gap="4px" flex={"1"} justifyContent={"flex-end"} alignItems={"center"}>
                        <Text
                          size="small"
                          colorModifier="grey"
                          modifier={"bold"}
                          className={"oc-shrink-0"}
                        >
                          {appstrp("remaining", ":")}
                        </Text>
                        <Tooltip
                          message={formatBudgetValue(
                            remainingBudgetForEmployee,
                            isEmployeeBudgetMasked
                          )}
                        >
                          <Text
                            size="small"
                            colorModifier={
                              remainingBudgetForEmployee !== null && remainingBudgetForEmployee < 0
                                ? "error"
                                : "grey"
                            }
                            modifier={"bold"}
                          >
                            {formatBudgetValue(
                              remainingBudgetForEmployee,
                              isEmployeeBudgetMasked,
                              shortNumberFormatOptions
                            )}
                          </Text>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </Flex>
                </Card>
              );
            })}
          </Flex>
        </div>
      )}
    </Flex>
  );
};

export default BudgetDetailsDrawer;
